<template>
  <Transition name="release-banner">
    <div
      v-if="shouldShowBanner"
      class="fixed inset-x-4 bottom-4 z-50 sm:left-auto sm:right-4 sm:w-full sm:max-w-md"
      role="status"
      aria-live="polite"
    >
      <div class="rounded-2xl border border-amber-200 bg-amber-50/95 p-4 shadow-2xl backdrop-blur dark:border-amber-900/80 dark:bg-amber-950/95">
        <div class="flex flex-col gap-3">
        <div>
          <p class="text-sm font-semibold text-amber-900 dark:text-amber-100">New app release available</p>
          <p class="text-sm text-amber-800 dark:text-amber-200">
            {{ latestReleaseLabel }} has been released. Refresh this screen to load the latest build.
          </p>
        </div>
        <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" size="sm" title="Later" class="justify-center" @click="dismissUpdate" />
          <Button variant="primary" size="sm" title="Refresh now" class="justify-center" @click="refreshApp" />
        </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import Button from "~/components/common/Button.vue";
import {
  buildAppReleaseInfo,
  formatReleaseDisplayName,
  isReleaseUpdateAvailable,
  type AppReleaseInfo,
} from "~/utils/appRelease";

const DISMISSED_RELEASE_STORAGE_KEY = "bp-dismissed-release-id";

const { public: config } = useRuntimeConfig();

// currentRelease is set once from the first successful _release.json fetch (the baseline).
// It is never updated after that — only latestRelease changes on subsequent polls.
// The banner fires only when latestRelease diverges from the baseline the user loaded with,
// which correctly represents "a new frontend deploy happened while you had the page open".
// Keeping both values uninitialised until the first fetch also prevents false positives from
// SSR/Cloud Run build metadata differing from the Firebase Hosting static build metadata.
const currentRelease = ref<AppReleaseInfo>(buildAppReleaseInfo({}));
const latestRelease = ref<AppReleaseInfo>(buildAppReleaseInfo({}));
const dismissedReleaseId = ref("");
const hasReleaseBaseline = ref(false);

const shouldShowBanner = computed(() => {
  if (import.meta.dev) return false;
  if (!hasReleaseBaseline.value) return false;
  return isReleaseUpdateAvailable(currentRelease.value, latestRelease.value)
    && latestRelease.value.releaseId !== dismissedReleaseId.value;
});

const latestReleaseLabel = computed(() => formatReleaseDisplayName(latestRelease.value));

let pollTimer: ReturnType<typeof setInterval> | undefined;

async function checkForUpdate() {
  try {
    // Fetch _release.json from Firebase Hosting (edge-cached static asset, not Cloud Run).
    // Cache-control headers on the Hosting rule ensure clients see the latest deploy quickly.
    const nextRelease = await $fetch<AppReleaseInfo>("/_release.json", {
      cache: "no-store",
      headers: {
        "cache-control": "no-cache",
      },
    });

    const normalized = buildAppReleaseInfo(nextRelease);
    latestRelease.value = normalized;

    if (!hasReleaseBaseline.value) {
      // First successful fetch: anchor currentRelease to what Hosting says right now.
      // All future polls compare against this value to detect new deploys.
      currentRelease.value = normalized;
      hasReleaseBaseline.value = true;
    }
  } catch (error) {
    console.warn("[release-update] Unable to check for a newer release.", error);
  }
}

function dismissUpdate() {
  dismissedReleaseId.value = latestRelease.value.releaseId;
  window.localStorage.setItem(DISMISSED_RELEASE_STORAGE_KEY, dismissedReleaseId.value);
}

function refreshApp() {
  window.location.reload();
}

function handleVisibilityChange() {
  if (document.visibilityState === "visible") {
    void checkForUpdate();
  }
}

onMounted(() => {
  dismissedReleaseId.value = window.localStorage.getItem(DISMISSED_RELEASE_STORAGE_KEY) || "";

  void checkForUpdate();

  // Poll the release endpoint so long-lived sessions learn about new deploys.
  const pollMs = Math.max(Number(config.appUpdatePollMs) || 300000, 30000);
  pollTimer = setInterval(() => {
    void checkForUpdate();
  }, pollMs);

  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onBeforeUnmount(() => {
  if (pollTimer) {
    clearInterval(pollTimer);
  }

  document.removeEventListener("visibilitychange", handleVisibilityChange);
});
</script>

<style scoped>
.release-banner-enter-active,
.release-banner-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.release-banner-enter-from,
.release-banner-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>